import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors } from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { CheckboxItem } from '@shared/models/step.model';
import { I18nService } from '@app/i18n';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { MatExpansionPanel } from '@angular/material/expansion';

let identifier: number = 0;

@Component({
  selector: 'pl-checkbox',
  templateUrl: './pl-checkbox.component.html',
  styleUrls: ['./pl-checkbox.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlCheckboxComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public label: string = '';
  @Input() public isDisabled: boolean = false;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public maxSelected: number | null = null;
  @Input() public minItemsDisplayed: number = 0;
  @Input() public minSelected: number | null = null;

  public selectMessage: string = '';

  public items: CheckboxItem[] = [];

  public identifier: string = `form-pl-checkbox-${identifier++}`;

  private onChangeCallback!: (value: CheckboxItem[]) => void;
  private onTouchedCallback!: () => void;

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly IconFrame: typeof IconFrame = IconFrame;

  public isPanelOpen: boolean = false;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    if (this.checkboxItems && this.checkboxItems.length > 0) {
      // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
      this.checkboxItems.first.nativeElement.focus({ preventScroll: true });
    }
  }

  @ViewChild(MatExpansionPanel) public matExpansionPanel!: MatExpansionPanel;
  @ViewChildren('checkboxItems', { read: ElementRef }) public checkboxItems!: QueryList<ElementRef>;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private i18nService: I18nService,
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        control.setValidators([this.validate.bind(this)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.selectMessage = this.getSelectMessage(this.minSelected, this.maxSelected);
  }

  private validate(): ValidationErrors | null {
    const allErrors: any = {};

    const numberBoxSelected: number = this.items.reduce((accumulator: number, currentValue: CheckboxItem) => {
      return accumulator + (currentValue.checked ? 1 : 0);
    }, 0);

    if (this.minSelected !== null) {
      if (this.maxSelected !== null) {
        // this.minSelected !== null && this.maxSelected !== null
        if (numberBoxSelected < this.minSelected || numberBoxSelected > this.maxSelected) {
          allErrors.minMax = true;
        }
      } else {
        // this.minSelected !== null && this.maxSelected === null
        if (numberBoxSelected < this.minSelected) {
          allErrors.min = true;
        }
      }
    } else {
      // this.minSelected === null
      if (this.maxSelected !== null && numberBoxSelected > this.maxSelected) {
        allErrors.max = true;
      }
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: CheckboxItem[]) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(itemsToWrite: CheckboxItem[] | null): void {
    if (itemsToWrite !== undefined && itemsToWrite !== null) {
      this.items = itemsToWrite.map((item: CheckboxItem) => {
        return { ...item };
      });
      this.displayAllCheckBoxIfItemsAlreadyCheckedAreNotVisible(itemsToWrite);
    }
    this.updateValueAndValidity();
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onCheckboxToggle(): void {
    this.emitChanges();
  }

  private emitChanges(): void {
    this.onChangeCallback(this.items);
    this.onTouched();
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }

  private getSelectMessage(minChecked: number | null, maxChecked: number | null): string {
    if (minChecked !== null || maxChecked !== null) {
      if (minChecked === maxChecked) {
        return this.i18nService.translate('blocks.checkbox.please_select_x_options', { count: minChecked });
      } else if (minChecked === null) {
        return this.i18nService.translate('blocks.checkbox.please_select_max_options', { count: maxChecked });
      } else if (maxChecked === null) {
        return this.i18nService.translate('blocks.checkbox.please_select_min_options', { count: minChecked });
      } else {
        return this.i18nService.translate('blocks.checkbox.please_select_range_options', { minChecked, maxChecked });
      }
    }
    return '';
  }

  public toggleExpansionPanel(): void {
    this.matExpansionPanel.toggle();
  }

  private displayAllCheckBoxIfItemsAlreadyCheckedAreNotVisible(items: CheckboxItem[]): void {
    for (const [index, item] of items.entries()) {
      if (item.checked && index >= this.minItemsDisplayed) {
        this.isPanelOpen = true;
        return;
      }
    }
  }
}
